import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { EmptyState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Page<T> {
  items: T[];
}
interface Alert {
  id: string;
  severity: string;
  status: string;
  title: string;
  source: string;
  createdAt: string;
}
interface Deployment {
  id: string;
  status: string;
}
const up = (s: string | null | undefined): string => (s ?? '').toUpperCase();
const isActiveAlert = (a: Alert): boolean =>
  up(a.status) !== 'RESOLVED' && up(a.status) !== 'CLOSED';

/**
 * Operations domain landing page.
 *
 * Provides real operational value — a triage summary and recent activity with direct
 * links — rather than repeating the sidebar. Every panel is permission-gated and
 * fails soft, so a user sees exactly the operational surfaces they can act on.
 */
export function OperationsOverview(): JSX.Element {
  const t = useT();
  const { can } = useAuth();

  const alerts = useAsync(
    () =>
      can('alert:read')
        ? api.get<Page<Alert>>('/alerts?limit=50')
        : Promise.resolve<Page<Alert> | null>(null),
    [],
  );
  const deployments = useAsync(
    () =>
      can('deployment:read')
        ? api.get<Page<Deployment>>('/deployments?limit=50')
        : Promise.resolve<Page<Deployment> | null>(null),
    [],
  );
  const drift = useAsync(
    () =>
      can('deployment:read')
        ? api.get<unknown[]>('/drift')
        : Promise.resolve<unknown[] | null>(null),
    [],
  );

  const active = (alerts.data?.items ?? []).filter(isActiveAlert);
  const critical = active.filter((a) => up(a.severity) === 'CRITICAL').length;
  const failed = (deployments.data?.items ?? []).filter((d) => up(d.status) === 'FAILED').length;
  const driftCount = Array.isArray(drift.data) ? drift.data.length : 0;
  const recent = active.slice(0, 6);

  return (
    <section>
      <div className="page-head">
        <h1>{t('nav.groups.operations')}</h1>
      </div>

      <div className="tiles">
        <Link className="tile card--link" to="/alerts">
          <span className="tile__label">{t('ops.activeAlerts')}</span>
          <span className="tile__value">{can('alert:read') ? active.length : '—'}</span>
          {critical > 0 ? (
            <span className="chip chip--critical">
              {t('ops.critical').replace('{count}', String(critical))}
            </span>
          ) : null}
        </Link>
        <Link className="tile card--link" to="/deployments">
          <span className="tile__label">{t('ops.failedDeployments')}</span>
          <span className="tile__value">{can('deployment:read') ? failed : '—'}</span>
        </Link>
        <Link className="tile card--link" to="/drift">
          <span className="tile__label">{t('ops.drift')}</span>
          <span className="tile__value">{can('deployment:read') ? driftCount : '—'}</span>
        </Link>
      </div>

      <div className="card">
        <div className="page-head">
          <h2>{t('ops.recentAlerts')}</h2>
          <Link className="btn btn--ghost" to="/alerts">
            {t('ops.viewAll')}
          </Link>
        </div>
        {!can('alert:read') ? (
          <EmptyState hint={t('states.permissionDenied')} />
        ) : alerts.loading ? (
          <Loading />
        ) : recent.length === 0 ? (
          <EmptyState hint={t('ops.noAlerts')} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('ops.severity')}</th>
                  <th>{t('ops.alert')}</th>
                  <th>{t('ops.source')}</th>
                  <th>{t('ops.when')}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span
                        className={`chip ${up(a.severity) === 'CRITICAL' ? 'chip--critical' : 'chip--warning'}`}
                      >
                        {a.severity}
                      </span>
                    </td>
                    <td>{a.title}</td>
                    <td>{a.source}</td>
                    <td>{new Date(a.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>{t('ops.quickLinks')}</h2>
        <div className="row-actions">
          <Link className="btn btn--ghost" to="/diagnostics">
            {t('nav.diagnostics')}
          </Link>
          <Link className="btn btn--ghost" to="/reports">
            {t('nav.reports')}
          </Link>
          <Link className="btn btn--ghost" to="/audit">
            {t('nav.audit')}
          </Link>
        </div>
      </div>
    </section>
  );
}
